import {
  any,
  array,
  literal,
  nullable,
  number,
  object,
  pipe,
  string,
  transform,
  union,
} from 'valibot';
import { IdSchema } from './id-schema';

export type ArticleBlock =
  | FirstHeadingBlock
  | SecondHeadingBlock
  | ThirdHeadingBlock
  | TextBlock
  | MarkdownBlock
  | OrderedListBlock
  | UnorderedListBlock
  | YouTubeVideoBlock
  | ImageListBlock
  | ImageBlock
  | ButtonLinkBlock
  | PanelBlock;

/**
 * @private
 */
export interface ArticleBlockBase {
  /**
   * ID of the block.
   */
  readonly id: string;

  /**
   * Article block order.
   */
  readonly order: number;
}

/**
 * First heading block.
 *
 * @public
 */
export interface FirstHeadingBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'heading-1';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * Heading itself.
     */
    readonly value: string;
  };
}

/**
 * @internal
 */
export const FirstHeadingBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('heading-1'),
    }),
    data: object({
      value: string(),
    }),
  }),
  transform(
    (input): FirstHeadingBlock => ({
      id: input.id,
      order: input.order,
      type: 'heading-1',
      data: {
        value: input.data.value,
      },
    })
  )
);

/**
 * Second heading block.
 *
 * @public
 */
export interface SecondHeadingBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'heading-2';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * Heading itself.
     */
    readonly value: string;
  };
}

/**
 * @internal
 */
export const SecondHeadingBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('heading-2'),
    }),
    data: object({
      value: string(),
    }),
  }),
  transform(
    (input): SecondHeadingBlock => ({
      id: input.id,
      order: input.order,
      type: 'heading-2',
      data: {
        value: input.data.value,
      },
    })
  )
);

/**
 * Third heading block.
 *
 * @public
 */
export interface ThirdHeadingBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'heading-3';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * Heading itself.
     */
    readonly value: string;
  };
}

/**
 * @internal
 */
export const ThirdHeadingBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('heading-3'),
    }),
    data: object({
      value: string(),
    }),
  }),
  transform(
    (input): ThirdHeadingBlock => ({
      id: input.id,
      order: input.order,
      type: 'heading-3',
      data: {
        value: input.data.value,
      },
    })
  )
);

/**
 * Text block.
 *
 * @public
 */
export interface TextBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'text';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * Text.
     */
    readonly value: string;

    /**
     * Apperance.
     */
    readonly appearance: 'primary' | 'secondary';
  };
}

/**
 * @internal
 */
export const TextBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('text'),
    }),
    data: object({
      value: string(),
      appearance: union([literal('primary'), literal('secondary')]),
    }),
  }),
  transform(
    (input): TextBlock => ({
      id: input.id,
      order: input.order,
      type: 'text',
      data: {
        value: input.data.value,
        appearance: input.data.appearance,
      },
    })
  )
);

/**
 * Panel block.
 *
 * @public
 */
export interface PanelBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'panel';

  /**
   * Panel block data.
   */
  readonly data: {
    /**
     * Apperance.
     */
    readonly appearance: 'primary' | 'secondary';

    /**
     * Size.
     */
    readonly size: 'lg' | 'md' | 'sm';
  };

  /**
   * Children Blocks.
   */
  readonly children: readonly ArticleBlock[];
}

/**
 * @internal
 */
export const PanelBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('panel'),
    }),
    data: object({
      appearance: union([literal('primary'), literal('secondary')]),
      size: union([literal('lg'), literal('md'), literal('sm')]),
      children: array(any()),
    }),
  }),
  transform(
    (input): PanelBlock => ({
      id: input.id,
      order: input.order,
      type: 'panel',
      data: {
        appearance: input.data.appearance,
        size: input.data.size,
      },
      children: input.data.children,
    })
  )
);

/**
 * Markdown block.
 *
 * @public
 */
export interface MarkdownBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'markdown';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * Markdown.
     */
    readonly value: string;
  };
}

/**
 * @internal
 */
export const MarkdownBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('markdown'),
    }),
    data: object({
      value: string(),
    }),
  }),
  transform(
    (input): MarkdownBlock => ({
      id: input.id,
      order: input.order,
      type: 'markdown',
      data: {
        value: input.data.value,
      },
    })
  )
);

/**
 * Ordered list block.
 *
 * @public
 */
export interface OrderedListBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'ordered-list';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * List items.
     */
    readonly items: readonly string[];

    /**
     * Start order.
     */
    readonly startOrder: number;
  };
}

/**
 * @internal
 */
export const OrderedListBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('ordered-list'),
    }),
    data: object({
      items: array(string()),
      start_order: number(),
    }),
  }),
  transform(
    (input): OrderedListBlock => ({
      id: input.id,
      order: input.order,
      type: 'ordered-list',
      data: {
        items: input.data.items,
        startOrder: input.data.start_order,
      },
    })
  )
);

/**
 * Unordered list block.
 *
 * @public
 */
export interface UnorderedListBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'unordered-list';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * List items.
     */
    readonly items: readonly string[];
  };
}

export const UnorderedListBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('unordered-list'),
    }),
    data: object({
      items: array(string()),
    }),
  }),
  transform(
    (input): UnorderedListBlock => ({
      id: input.id,
      order: input.order,
      type: 'unordered-list',
      data: {
        items: input.data.items,
      },
    })
  )
);

/**
 * YouTube video block.
 *
 * @public
 */
export interface YouTubeVideoBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'youtube-video';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * YouTube media ID. e.g. `https://www.youtube.com/embed/h_D3VFfhvs4` where ID is `h_D3VFfhvs4`.
     */
    readonly id: string;
  };
}

/**
 * @internal
 */
export const YouTubeVideoBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('youtube-video'),
    }),
    data: object({
      id: string(),
    }),
  }),
  transform(
    (input): YouTubeVideoBlock => ({
      id: input.id,
      order: input.order,
      type: 'youtube-video',
      data: {
        id: input.data.id,
      },
    })
  )
);

/**
 * Image list block.
 *
 * @public
 */
export interface ImageListBlock extends ArticleBlockBase {
  /**
   *
   * Article block type.
   */
  readonly type: 'image-list';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * List items.
     */
    readonly items: ReadonlyArray<{
      /**
       * Image caption (optional).
       */
      readonly caption: string | null;

      /**
       * ID of the media.
       */
      readonly mediaId: number;
    }>;
  };
}

/**
 * @internal
 */
export const ImageListBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('image-list'),
    }),
    data: object({
      items: array(
        object({
          caption: nullable(string()),
          media_id: number(),
        })
      ),
    }),
  }),
  transform(
    (input): ImageListBlock => ({
      id: input.id,
      order: input.order,
      type: 'image-list',
      data: {
        items: input.data.items.map((item) => ({
          caption: item.caption,
          mediaId: item.media_id,
        })),
      },
    })
  )
);

/**
 * Image block.
 *
 * @public
 */
export interface ImageBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'image';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * Image caption (optional).
     */
    readonly caption: string | null;

    /**
     * ID of the media.
     */
    readonly mediaId: number;
  };
}

/**
 * @internal
 */
export const ImageBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('image'),
    }),
    data: object({
      caption: nullable(string()),
      media_id: number(),
    }),
  }),
  transform(
    (input): ImageBlock => ({
      id: input.id,
      order: input.order,
      type: 'image',
      data: {
        caption: input.data.caption,
        mediaId: input.data.media_id,
      },
    })
  )
);

/**
 * Button link block.
 *
 * @public
 */
export interface ButtonLinkBlock extends ArticleBlockBase {
  /**
   * Article block type.
   */
  readonly type: 'button-link';

  /**
   * Article block data.
   */
  readonly data: {
    /**
     * Button text (max 50 characters).
     */
    readonly text: string;

    /**
     * Apperance.
     */
    readonly appearance: 'primary' | 'outlined';

    /**
     * Size.
     */
    readonly size: 'md' | 'sm';

    /**
     * Url.
     */
    readonly url: string;
  };
}

/**
 * @internal
 */
export const ButtonLinkBlockSchema = pipe(
  object({
    id: IdSchema,
    order: number(),
    widget: object({
      key: literal('button-link'),
    }),
    data: object({
      text: string(),
      url: string(),
      size: union([literal('md'), literal('sm')]),
      appearance: union([literal('primary'), literal('outlined')]),
    }),
  }),
  transform(
    (input): ButtonLinkBlock => ({
      id: input.id,
      order: input.order,
      type: 'button-link',
      data: {
        text: input.data.text,
        url: input.data.url,
        size: input.data.size,
        appearance: input.data.appearance,
      },
    })
  )
);

/**
 * @internal
 */
export const ArticleBlockSchema = union([
  FirstHeadingBlockSchema,
  SecondHeadingBlockSchema,
  ThirdHeadingBlockSchema,
  TextBlockSchema,
  MarkdownBlockSchema,
  OrderedListBlockSchema,
  UnorderedListBlockSchema,
  YouTubeVideoBlockSchema,
  ImageListBlockSchema,
  ImageBlockSchema,
  ButtonLinkBlockSchema,
  PanelBlockSchema,
]);
